import React, { useEffect, useRef, useState } from "react";
import { Outlet, useParams } from 'react-router-dom';
import DOMPurify from "dompurify";
import Layout from "../../layouts/Layout";
import Modal from "../../components/ui/Modal";
import NotFound from "../NotFound";
import SidebarNav from "./components/SidebarNav";
import SidebarDetails from "./components/SidebarDetails";
import { useLoading } from "../../context/LoadingContext";
import { getProcurementByUuid, getIsValidForNextStep, updateProcurement } from "../../services/api/procurementService";
import { getApprovalDescriptionByStep, Step } from "../../helpers/stepHelper";
import { toast } from "react-toastify";
import { useUserContext } from "../../context/userContext";
import { approveProcurement, requestProcurement } from "../../services/api/orchestratorService";
import { prettyIdProcurement } from "../../helpers/formatHelper";
import { DocumentType } from "../../helpers/documentHelper";
import SelectInput from "../../components/ui/inputs/SelectInput";
import DatetimeInput from "../../components/ui/inputs/DatetimeInput";
import { enforcePolicy } from "../../services/api/userService";
import { isAuthToApproval, isAuthToRequest } from "../../helpers/orchestratorHelper";
import Unauthorised from "../Unauthorised";
import { fromZonedTime } from "date-fns-tz";
import { cleanQuillHtml, isEditorEmpty } from "../../helpers/editorHelper";
import Editor from "../../components/Editor";
import { Button } from "@mui/material";
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';

export default function Procurement() {
  const { user } = useUserContext();
  const { uuidProcurement } = useParams();
  const { setIsLoading } = useLoading();

  const [isNotFound, setIsNotFound] = useState(false);
  const [unauthorised, setUnauthorised] = useState(false);

  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [requestType, setRequestType] = useState('');
  const [requestDescription, setRequestDescription] = useState('');
  const [offerLimitAt, setOfferLimitAt] = useState('');

  const [procurement, setProcurement] = useState(null);

  const quillRef = useRef();

  useEffect(() => {
    async function fetch() {
      try {
        setIsLoading(true);
        await handleUpdateProcurement();
      } catch (error) {
        console.error(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetch();
    // eslint-disable-next-line
  }, [uuidProcurement]);

  async function handleUpdateProcurement() {
    try {
      const resultProcurementPromise = getProcurementByUuid(uuidProcurement);
      const canWritePromise = enforcePolicy(user.uuid, `/procurement/${uuidProcurement}`, "POST");
      const canWriteParamsPromise = enforcePolicy(user.uuid, `/procurement/params`, "POST");
      const isValidForNextStepPromise = getIsValidForNextStep(uuidProcurement);
      const [resultProcurement, canWrite, canWriteParams, validator] = await Promise.all([resultProcurementPromise, canWritePromise, canWriteParamsPromise, isValidForNextStepPromise]);
      resultProcurement.canWrite = canWrite;
      resultProcurement.canWriteParams = canWriteParams;
      resultProcurement.isValidForNextStep = validator.isValidForNextStep;
      setProcurement(resultProcurement);
      setOfferLimitAt(resultProcurement.offerLimitAt);
      return resultProcurement;
    } catch (error) {
      if (error.status === 401) {
        setUnauthorised(true);
      } else {
        setIsNotFound(true);
      }
    }
  }

  function closeModals() {
    setIsApproveModalOpen(false);
    setIsRequestModalOpen(false);
  }

  async function handleApproveProcurement() {
    if (procurement.step.name === Step.VALIDATION_OFFER_REQUEST_EMAIL) {
      if (offerLimitAt === "") return;
    }
    closeModals();
    setIsLoading(true);

    try {

      if (procurement.step.name === Step.WRITING_SPECIFICATIONS) {
        if (!procurement.submittedAt) {
          const utcDate = fromZonedTime(new Date(), Intl.DateTimeFormat().resolvedOptions().timeZone);
          await updateProcurement(procurement.uuid, { submittedAt: utcDate });
        }
        updateProcurement(procurement.uuid, {
          documentsOptions: [
            {
              type: DocumentType.SPECIFICATIONS,
              format: "word",
            },
            {
              type: DocumentType.LAUNCH_NOTE,
              format: "word",
            },
          ],
        });
      }

      if (procurement.step.name === Step.WRITING_ATTRIBUTION) {
        updateProcurement(procurement.uuid, {
          documentsOptions: [
            {
              type: DocumentType.ATTRIBUTION,
              format: "word",
            },
            {
              type: DocumentType.ATTRIBUTION_NOTIFICATION_UNSIGNED,
              format: "word",
            },
          ],
        });
      }
    } catch (error) {
      console.error(error);
    }

    try {
      if (procurement.step.name === Step.VALIDATION_OFFER_REQUEST_EMAIL) {
        if (procurement.offerLimitAt) {
          const utcDate = fromZonedTime(offerLimitAt, Intl.DateTimeFormat().resolvedOptions().timeZone);
          await updateProcurement(procurement.uuid, { offerLimitAt: utcDate });
        }
      }

      // if (procurement.step.name === Step.VALIDATION_OFFER_REQUEST_EMAIL || procurement.step.name === Step.VALIDATION_ENCODING_E_PROCUREMENT) {
      //   await convertWordDocumentsToPdf(procurement.uuid, [
      //     {
      //       type: DocumentType.SPECIFICATIONS,
      //     },
      //     {
      //       type: DocumentType.LAUNCH_NOTE,
      //     },
      //   ]);
      // }

      await approveProcurement(procurement.uuid);
      // const procurementUpdated = await handleUpdateProcurement();

      // if (procurementUpdated.step.name === Step.VALIDATION_OFFER_REQUEST_EMAIL || procurementUpdated.step.name === Step.VALIDATION_ENCODING_E_PROCUREMENT) {
      //   updateProcurement(procurement.uuid, {
      //     documentsOptions: [
      //       {
      //         type: DocumentType.SPECIFICATIONS,
      //       },
      //       {
      //         type: DocumentType.LAUNCH_NOTE,
      //       },
      //     ],
      //   });
      // }

      toast.success("Réponse envoyée.", { autoClose: 1000 });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleRequestMoreInfo() {
    if (isEditorEmpty(requestDescription)) {
      return;
    }
    closeModals();
    try {
      setIsLoading(true);
      await updateProcurement(uuidProcurement,
        {
          comment: { message: cleanQuillHtml(requestDescription) },
        },
      );
      await requestProcurement(procurement.uuid, cleanQuillHtml(requestDescription), requestType);
      await handleUpdateProcurement();
      toast.success("Demande envoyée.");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  }

  if (unauthorised) {
    return (
      <Unauthorised />
    );
  }

  if (isNotFound) {
    return (
      <NotFound />
    );
  }

  if (!procurement || !user) {
    return <Layout />;
  }

  return (
    <Layout>
      <SidebarNav
        bidders={procurement.enterprises}
        status={procurement.step.name}
        commentsTotal={procurement.comments.length}
        canAccessParams={procurement.canWrite || procurement.canWriteParams}
        hasMissingRequiredFields={!procurement.isValidForNextStep}
      />

      <SidebarDetails
        procurement={procurement}
      >
        {
          isAuthToApproval(procurement)
            ? (
              <>
                <Button
                  color="success"
                  variant="contained"
                  disableElevation
                  onClick={() => setIsApproveModalOpen(true)}
                  disabled={!procurement.isValidForNextStep}
                  loading={procurement.isGenerating}
                  startIcon={<CheckIcon />}
                  fullWidth
                  sx={{
                    '& .MuiButton-startIcon': {
                      position: 'absolute',
                      left: '16px',
                      marginRight: 0,
                    },
                  }}

                >
                  Valider
                </Button>
                {!procurement.isValidForNextStep && <span className="text-red-600 italic text-sm font-semibold">Certains champs obligatoires doivent être complétés dans l’édition pour valider le formulaire !</span>}
              </>
            )
            : null
        }
        {
          isAuthToRequest(procurement)
            ? (
              <Button
                color="primary"
                variant="contained"
                disableElevation
                onClick={() => setIsRequestModalOpen(true)}
                disabled={procurement.isGenerating}
                startIcon={<ClearIcon />}
              >
                Demander des modifications
              </Button>
            )
            : null
        }
        {procurement.isGenerating && <span className="text-gray-500 italic text-sm mt-2">Traitement en cours...</span>}
      </SidebarDetails>

      <div className="flex flex-col pt-16 px-5 py-4 lg:pt-4 lg:mx-72 h-full overflow-y-auto">
        <h1 className="text-2xl mb-6 lg:text-4xl font-semibold">
          {prettyIdProcurement(procurement.id)} {procurement.name}
        </h1>
        <Outlet context={{ procurement, handleUpdateProcurement }} />
      </div>

      {isApproveModalOpen && (
        <Modal
          title="Confirmation"
          onClose={closeModals}
          footer={(
            <>
              <Button
                color="success"
                variant="contained"
                disableElevation
                onClick={handleApproveProcurement}
              >
                <span>Valider</span>
              </Button>
              <Button
                color="primary"
                variant="contained"
                disableElevation
                onClick={closeModals}
              >
                <span>
                  Annuler
                </span>
              </Button>
            </>
          )}
        >
          <div className="min-h-28 p-4 space-y-4">
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(getApprovalDescriptionByStep[procurement.step.name]) }} />
            {procurement.step.name === Step.VALIDATION_OFFER_REQUEST_EMAIL && procurement.offerLimitAt
              && (
                <div className="space-y-4">
                  <p className="text-red-400 font-bold">Assurez-vous que la date limite de remise des offres correspond à celle du cahier des charges. Si elle est différente, sélectionnez la date inscrite dans le cahier des charges dans le sélecteur ci-dessous :</p>
                  <div className="max-w-72">
                    <DatetimeInput key="offerLimitAt" label="Date limite de remise des offres" value={offerLimitAt} onChange={setOfferLimitAt} required />
                  </div>
                </div>
              )}
            {/* {(procurement.step.name === Step.VALIDATION_ENCODING_E_PROCUREMENT || procurement.step.name === Step.VALIDATION_OFFER_REQUEST_EMAIL)
              && (
                <p className="text-red-400 font-bold">Assurez-vous d'avoir sauvegardé et fermé les documents. Ceux-ci vont être repris et convertis en PDF pour la suite du processus. </p>
              )} */}
          </div>
        </Modal>
      )}

      {isRequestModalOpen && (
        <Modal
          title="Demande de modification"
          onClose={closeModals}
          footer={(
            <>
              <Button
                color="success"
                variant="contained"
                disableElevation
                onClick={handleRequestMoreInfo}
              >
                <span>Valider</span>
              </Button>
              <Button
                color="primary"
                variant="contained"
                disableElevation
                onClick={closeModals}
              >
                <span>
                  Annuler
                </span>
              </Button>
            </>
          )}
        >
          <div className="p-4 space-y-2">
            {(procurement.step.name === Step.APPROVAL_SPECIFICATIONS || procurement.step.name === Step.APPROVAL_SPECIFICATIONS_BY_BOD)
              && <SelectInput id="type" label="Sur quel élément porte la demande ?" value={requestType} onChange={setRequestType} options={[{ label: "Précision sur le contenu", value: "content" }, { label: "Structure et mise en forme", value: "layout" }]} required />}
            <div className="h-60">
              <Editor
                ref={quillRef}
                onTextChange={setRequestDescription}
              />
            </div>
          </div>
        </Modal>
      )}

    </Layout>
  );
}
